import { Button, Loader, ScrollArea, Text } from '@mantine/core';
import { api } from '~/trpc-extra/api';
import DiffViewer from '../test/diffViewer/DiffViewer';
import { modals } from '@mantine/modals';
import { useRouter } from 'next/router';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useTestSuiteDialog } from '~/hooks/useTestSuiteDialog';
import { notifications } from '@mantine/notifications';

export function VersionCompareTrunk({ testId, testDraftId }: { testId: string; testDraftId: string }) {
  const projectId = useProjectId();
  const router = useRouter();
  const trpcUtils = api.useUtils();

  const { data: test, isLoading: isTestLoading } = api.test.getTest.useQuery({ id: testId });
  const { data: draftContent, isLoading: isDraftLoading } = api.testContent.get.useQuery(
    { id: testDraftId },
    { enabled: !!testDraftId },
  );
  const { data: trunkContent, isLoading: isTrunkLoading } = api.testContent.get.useQuery(
    { id: test?.trunkContentId || '' },
    { enabled: !!test?.trunkContentId },
  );

  const { data: projectAdminInfo } = api.project.getProjectAdminInfo.useQuery(
    { projectId: projectId || '' },
    { enabled: !!projectId },
  );

  const extractTestKnowledgeMutation = api.knowledge.extractKnowledge.useMutation({
    onSuccess: ({ count }) => {
      if (count > 0) {
        notifications.show({
          title: 'Knowledge Extracted',
          message: `Extracted ${count} knowledge item${count === 1 ? '' : 's'} from published test`,
          color: 'teal',
        });
      }
    },
    onError: (error) => {
      console.error('Error extracting test knowledge:', error);
      notifications.show({
        title: 'Knowledge Extraction Failed',
        message: error.message || 'Failed to extract knowledge from test',
        color: 'red',
      });
    },
  });

  const { mutate: publishTestDraft, isPending: isPublishing } = api.testDraft.publishTestDraft.useMutation({
    onSuccess: (publishedId) => {
      router.push(`/project/${projectId}/test/${publishedId.testId}`).catch(() => undefined);

      if (projectAdminInfo?.extractKnowledgeFromPublishedTests) {
        extractTestKnowledgeMutation.mutate({ testId: publishedId.testId });
      }
    },
    onSettled: () => {
      modals.closeAll();
    },
  });

  const { mutate: addToTestSuiteAndPublish, isPending: isAddingToTestSuite } =
    api.testDraft.addToTestSuiteAndPublish.useMutation({
      onMutate: () => {
        notifications.show({
          title: 'Adding to test suite and publish',
          message: 'Please wait...',
        });
      },
      onSuccess: async () => {
        notifications.show({
          title: 'Added to test suite',
          message: 'Test has been added to test suite and published',
        });
        trpcUtils.testDraft.listByTestId.invalidate().catch(() => undefined);
        trpcUtils.testDraft.list.invalidate().catch(() => undefined);
        trpcUtils.test.getTest.invalidate().catch(() => undefined);
        trpcUtils.testContent.get.invalidate().catch(() => undefined);
        trpcUtils.testSuite.list.invalidate().catch(() => undefined);
        await router.push(`/project/${projectId}/test/${testId}`);

        if (projectAdminInfo?.extractKnowledgeFromPublishedTests) {
          extractTestKnowledgeMutation.mutate({ testId });
        }
      },
    });

  const addToTestSuite = useTestSuiteDialog({
    onSuiteClick: (testSuiteId) => {
      addToTestSuiteAndPublish({
        testContentId: testDraftId,
        testId,
        testSuiteId,
      });
    },
  });

  const isLoading = isTestLoading || isDraftLoading || isTrunkLoading;

  return (
    isLoading ?
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    : !draftContent || !trunkContent ?
      <div className="flex h-full items-center justify-center">
        <Text>Unable to load comparison data, please refresh or try again later</Text>
      </div>
    : <div className="flex h-full flex-col border-t border-gray-200 dark:border-neutral-700">
        <ScrollArea className="flex-1 px-6">
          <div className="p-6">
            <DiffViewer
              testId={testId}
              prevContent={{
                testContentId: trunkContent.id,
                steps: trunkContent.steps,
              }}
              newContent={{
                testContentId: draftContent.id,
                steps: draftContent.steps,
              }}
              prevStepsTitle={<Text className="font-medium">Published Version</Text>}
              newStepsTitle={<Text className="font-medium">Draft Version</Text>}
            />
          </div>
        </ScrollArea>
        <div className="flex w-full items-center justify-end gap-2 border-t border-gray-200 bg-gray-100 p-4 dark:border-neutral-700 dark:bg-neutral-800">
          <Button
            loading={isAddingToTestSuite}
            onClick={addToTestSuite}
            variant="light"
          >
            Add to Test Suite
          </Button>
          <Button
            loading={isPublishing}
            onClick={() => publishTestDraft({ testContentId: testDraftId })}
          >
            Publish Draft
          </Button>
        </div>
      </div>
  );
}
